import { Icon } from "@/components/icons/Icon"
import { useTranslation } from "react-i18next"
import { showContextMenu } from "@/components/ContextMenuManager"
import { ContextMenuItem, ContextMenuSeparator } from "@/components/ui/ContextMenu"
import { pushDialog, resetDialogs } from "@/store/dialogs-state"
import { updateAIStyleCover, updateAIStyleName } from "@/store/style/update-style"
import { ConfirmDialog } from "../Dialogs/ConfirmDialog"
import { StyleResponseDTO } from "@/api/dto/styles"
import {
	getDuplicateStyleNameErrorMessage,
	isDuplicateStyleNameError,
	StyleService,
} from "@/store/style/style-service"
import { isViewReferencesDebugEnabled } from "@/utils/measurement/feature-flag"
import { StyleReferencesDialog } from "../Dialogs/StyleReferencesDialog"
import { toast } from "react-hot-toast"
import { StyleNamingDialog } from "../Dialogs/StyleNamingDialog"
import { StyleUpdateCoverDialog } from "../Dialogs/StyleUpdateCoverDialog"
import { getAxiosBackendReason } from "@/utils/error"

interface UserStyleContextMenuProps {
	userStyle: StyleResponseDTO
	onStyleRenamed: (styleId: string, name: string) => void
	onStyleDeleted: (styleId: string) => void
	onReferenceDeleted: (styleId: string, referenceCount: number) => void
	onStyleDuplicated: (styleId: string) => void
	onStyleCoverChanged: (styleId: string, coverUrl: string) => void
}

export const UserStyleContextMenu = ({
	userStyle,
	onStyleRenamed,
	onStyleDeleted,
	onReferenceDeleted,
	onStyleDuplicated,
	onStyleCoverChanged,
}: UserStyleContextMenuProps) => {
	const { t } = useTranslation("components")
	const showRenameStyleDialog = (style: StyleResponseDTO) => {
		if (style) {
			pushDialog(StyleNamingDialog, {
				initialName: style.name,
				trackingViewName: "rename_style_dialog",
				title: t("userStyleContextMenu.renameTitle"),
				actionTitle: t("common:action.update"),
				onCompleteWithError: async (result: { name: string }) => {
					toast.loading(t("userStyleContextMenu.renaming"), {
						id: "rename_style",
						duration: Infinity,
					})
					try {
						await updateAIStyleName(style, result.name)
						toast.success(t("userStyleContextMenu.renamed"), {
							id: "rename_style",
							duration: 2000,
						})
						onStyleRenamed(style.styleId, result.name)
						resetDialogs()
					} catch (error) {
						toast.remove("rename_style")
						if (isDuplicateStyleNameError(error)) {
							return getDuplicateStyleNameErrorMessage(error.styleName)
						}
						const backendReason = getAxiosBackendReason(error)
						if (backendReason) {
							return backendReason
						}
						throw error
					}
				},
			})
		}
	}

	const showChangeCoverDialog = (style: StyleResponseDTO) => {
		if (style) {
			pushDialog(StyleUpdateCoverDialog, {
				trackingViewName: "update_style_cover",
				styleId: style.styleId,
				title: t("userStyleContextMenu.changeCoverTitle"),
				actionTitle: t("common:action.update"),
				onCompleteWithError: async (result: { cover: Blob; isPublic: boolean }) => {
					resetDialogs()
					toast.loading(t("userStyleContextMenu.updatingCover"), {
						id: "change_style_cover",
						duration: Infinity,
					})

					try {
						const coverImageUrl = await updateAIStyleCover(style, result.cover)
						toast.success(t("userStyleContextMenu.coverUpdated"), {
							id: "change_style_cover",
							duration: 2000,
						})

						onStyleCoverChanged(style.styleId, coverImageUrl)
					} catch (error) {
						const backendReason = getAxiosBackendReason(error)
						if (backendReason) {
							toast.error(backendReason, {
								id: "change_style_cover",
								duration: 3000,
							})
						} else {
							toast.dismiss("change_style_cover")
						}
					}
				},
			})
		}
	}

	const showDuplicateStyleDialog = (style: StyleResponseDTO) => {
		pushDialog(StyleNamingDialog, {
			initialName: style.name,
			trackingViewName: "duplicate_style_dialog",
			title: t("userStyleContextMenu.duplicateTitle"),
			actionTitle: t("userStyleContextMenu.duplicateAIStyle"),
			onCompleteWithError: async (result: { name: string }) => {
				toast.loading(t("userStyleContextMenu.duplicating"), {
					id: "duplicate_style",
					duration: Infinity,
				})

				try {
					const newStyle = await StyleService.duplicateStyle(style.styleId, result.name)
					toast.success(t("userStyleContextMenu.duplicated"), {
						id: "duplicate_style",
						duration: 2000,
					})
					onStyleDuplicated(newStyle.styleId)
					resetDialogs()
				} catch (error) {
					toast.remove("duplicate_style")
					if (isDuplicateStyleNameError(error)) {
						return getDuplicateStyleNameErrorMessage(error.styleName)
					}
					const backendReason = getAxiosBackendReason(error)
					if (backendReason) {
						return backendReason
					}
					throw error
				}
			},
		})
	}

	const showReferencesDialog = (style: StyleResponseDTO) => {
		pushDialog(StyleReferencesDialog, {
			style: style,
			trackingViewName: "view_references_dialog",
			fromProject: false,
			onReferenceDeleted: (referenceCount: number) => {
				onReferenceDeleted(style.styleId, referenceCount)
			},
		})
	}

	const showDeleteStyleDialog = (style: StyleResponseDTO) => {
		if (style) {
			pushDialog(ConfirmDialog, {
				title: t("userStyleContextMenu.deleteTitle"),
				message: t("userStyleContextMenu.deleteMessage", { name: style.name }),
				secondary: { title: t("common:action.cancel") },
				primary: {
					title: t("common:action.delete"),
					isDestructive: true,
					handler: () => {
						const styleIdForDeleting = style.styleId
						void StyleService.deleteStyle(styleIdForDeleting).then(() => {
							onStyleDeleted(styleIdForDeleting)
						})
					},
				},
				trackingViewName: "delete_style_confirmation",
			})
		}
	}

	const handleStyleContextMenu = (e: React.MouseEvent, style: StyleResponseDTO) => {
		e.stopPropagation()
		showContextMenu(
			e,
			<div aria-label={t("userStyleContextMenu.ariaUserStyleMenu")}>
				<ContextMenuItem
					trackingLabel="rename"
					onClick={() => showRenameStyleDialog(style)}
				>
					{t("userStyleContextMenu.renameAIStyle")}
				</ContextMenuItem>
				<ContextMenuItem
					trackingLabel="change_cover"
					aria-label={t("userStyleContextMenu.ariaChangeAIStyleCoverImage")}
					onClick={() => showChangeCoverDialog(style)}
				>
					{t("userStyleContextMenu.changeCover")}
				</ContextMenuItem>
				{isViewReferencesDebugEnabled() && (
					<ContextMenuItem
						trackingLabel="references"
						onClick={() => showReferencesDialog(style)}
					>
						{t("userStyleContextMenu.viewReferences")}
					</ContextMenuItem>
				)}
				<ContextMenuItem
					trackingLabel="duplicate"
					onClick={() => showDuplicateStyleDialog(style)}
				>
					{t("userStyleContextMenu.duplicateAIStyle")}
				</ContextMenuItem>
				<ContextMenuSeparator />
				<ContextMenuItem
					trackingLabel="delete"
					onClick={() => showDeleteStyleDialog(style)}
				>
					{t("userStyleContextMenu.deleteAIStyle")}
				</ContextMenuItem>
			</div>,
		)
	}

	return (
		<div className="h-9 shrink-0 rounded-sm bg-base-700 p-1 hover:bg-base-600">
			<Icon
				className="size-full"
				name="more"
				onClick={(e) => handleStyleContextMenu(e, userStyle)}
			/>
		</div>
	)
}
